import {
  CardStyleInterpolators,
  StackScreenProps,
} from "@react-navigation/stack";
import {
  PerformStockCountRootParamList,
  StockCountsRootParamList,
} from "../../../../types";
import {
  ModalCloseButton,
  createStackHeaderNavigator,
} from "@towersystems/roam-common-native";
import { StockCountScreen } from "../StockCountScreen";
import { StockCountActionsRootScreen } from "../StockCountActionsRootScreen";
import { CountProductModalRootScreen } from "../CountProductModalRootScreen";
import { PermissionsModalRootScreen } from "../../../permission/screens";
import colors from "react-native-ui-lib/src/style/colors";
import { StockCountScannerScreen } from "../StockCountScannerScreen";
import {
  CurrentCountProvider,
  StockCountSearchProvider,
} from "../../components";
import React from "react";
import { useLazyQuery } from "@apollo/client";
import { StockCount as StockCountType } from "@towersystems/roam-common/lib/generated-types";
import { STOCK_COUNT_DEEP_NESTING } from "../../../../utilities/stock-count";
import { useCreateOrUpdateEntity } from "../../components/StockCountSyncProvider/utils";
import StockCount from "../../../../utilities/data/model/StockCount";
import { stockCountDefinition } from "../../../../utilities/setup/definitions/stock-count-definition";
import { useStockCountSyncContext } from "../../../../utilities/stock-count/hooks";
import { StockCountScreenSkeleton } from "../StockCountScreen/components";
import { PlaceholderLoadingScreen } from "../../../products/screens/PlaceholderLoadingScreen";
import { StockCountItemHistoryRootScreen } from "../StockCountItemHistoryRootScreen";
import { useHasTransitionEnded } from "../../../../utilities/use-after-interactions";
import useOnline from "@towersystems/roam-common-native/lib/utilities/use-online";
import { useDatabase } from "@nozbe/watermelondb/hooks";

export type PerformStockCountRootScreenProps = StackScreenProps<
  StockCountsRootParamList,
  "PerformStockCountRootScreen"
>;

export const PerformStockCountRootScreen = ({
  navigation,
  route,
}: PerformStockCountRootScreenProps) => {
  const { createOrUpdateEntity } = useCreateOrUpdateEntity();
  const [, setSyncingCountedItemsComplete] = React.useState<0 | 1 | 2>(0);
  const { syncRemoteStockCountLines } = useStockCountSyncContext();
  const isOnline = useOnline();
  const database = useDatabase();

  const [stockCount, setStockCount] = React.useState<StockCount | undefined>(
    undefined,
  );

  const transitionEnded = useHasTransitionEnded(navigation);

  const Stack = createStackHeaderNavigator<PerformStockCountRootParamList>();
  const { stockCountId } = route.params;

  const [fetchStockCount] = useLazyQuery<{
    stockCount: StockCountType;
  }>(STOCK_COUNT_DEEP_NESTING);

  React.useEffect(() => {
    if (!isOnline) {
      // check if stock count already exists in local db
      (async () => {
        const localStockCount = await database
          .get<StockCount>(stockCountDefinition.config.entity)
          .find(stockCountId);
        setStockCount(localStockCount);
      })().catch((err) => {
        console.log(err);
      });
    } else {
      fetchStockCount({
        variables: {
          id: stockCountId,
        },
      })
        .then(async (res) => {
          if (res.data?.stockCount) {
            return createOrUpdateEntity<StockCountType, StockCount>(
              stockCountDefinition.config.entity,
              res.data.stockCount,
              stockCountDefinition.config.prepareEntity,
            ).then(async (model) => {
              setStockCount(model);
              return syncRemoteStockCountLines(res.data?.stockCount.id ?? "")
                .then(() => setSyncingCountedItemsComplete(1))
                .catch((err) => {
                  setSyncingCountedItemsComplete(2);
                });
            });
          } else {
            throw new Error("No stock count returned from query");
          }
        })
        .catch((err) => console.log(err));
    }
  }, []);

  React.useEffect(() => {
    const fetchData = () => {
      fetchStockCount({
        variables: {
          id: stockCountId,
        },
      })
        .then(async (res) => {
          if (res.data?.stockCount) {
            return createOrUpdateEntity<StockCountType, StockCount>(
              stockCountDefinition.config.entity,
              res.data.stockCount,
              stockCountDefinition.config.prepareEntity,
            ).then(async (model) => {
              return syncRemoteStockCountLines(res.data?.stockCount.id ?? "")
                .then(() => setSyncingCountedItemsComplete(1))
                .catch(() => {
                  setSyncingCountedItemsComplete(2);
                });
            });
          }
        })
        .catch((err) => console.log(err));
    };

    const interval = setInterval(fetchData, 10000); // Run every 10 seconds

    return () => clearInterval(interval); // Cleanup interval on unmount
  }, [stockCountId]); // Add dependencies to rerun effect when stockCountId changes

  React.useEffect(() => {
    if (stockCount) {
      navigation.setOptions({
        headerShown: false,
      });
    }
  }, [stockCount?.id]);

  if (!stockCount || !transitionEnded) {
    return (
      <PlaceholderLoadingScreen
        navigation={navigation}
        modifyHeader={false}
        content={StockCountScreenSkeleton}
      />
    );
  }

  return (
    <StockCountSearchProvider>
      <CurrentCountProvider stockCount={stockCount}>
        <Stack.Navigator
          screenOptions={{
            animationEnabled: true,
            headerLeft: () => <ModalCloseButton type="Back" />,
            title: "",
          }}
        >
          <Stack.Group>
            <Stack.Screen
              name="StockCountScreen"
              component={StockCountScreen}
            />
          </Stack.Group>

          <Stack.Group
            screenOptions={{
              presentation: "modal",
              headerShown: false,
              cardStyleInterpolator: CardStyleInterpolators.forVerticalIOS,
              headerLeft: () => <ModalCloseButton type="Close" />,
            }}
          >
            <Stack.Screen
              name="StockCountActionsRootScreen"
              component={StockCountActionsRootScreen}
              options={{
                headerShown: false,
              }}
            />

            <Stack.Screen
              name="CountProductModalRootScreen"
              component={CountProductModalRootScreen}
              options={{
                headerShown: false,
              }}
            />
            <Stack.Screen
              name="StockCountItemHistoryRootScreen"
              component={StockCountItemHistoryRootScreen}
              options={{
                headerShown: true,
              }}
            />
            <Stack.Screen
              name="StockCountScannerScreen"
              component={StockCountScannerScreen}
              options={{
                headerShown: false,
                cardStyle: { height: "100%", backgroundColor: "#000" },
              }}
            />
            <Stack.Screen
              name="PermissionsModalRootScreen"
              component={PermissionsModalRootScreen}
              options={{
                headerStyle: {
                  backgroundColor: colors.surface,
                },
                cardStyle: {
                  backgroundColor: "transparent",
                },
                headerShown: false,
                animationEnabled: true,
                presentation: "transparentModal",
                cardStyleInterpolator:
                  CardStyleInterpolators.forModalPresentationIOS,
              }}
            />
          </Stack.Group>
        </Stack.Navigator>
      </CurrentCountProvider>
    </StockCountSearchProvider>
  );
};
